import React, { FC, useEffect, useRef, useState } from "react";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useLazyGetFirmActivityQuery } from "../../slices/ActivityLogSlice";
import { ActivityCategory, ActivityLogEntry } from "./activityLog.types";
import { ACTIVITY_FILTERS } from "./activityLog.utils";
import { ActivityLogList } from "./ActivityLogList";

const PAGE_SIZE = 50;

export const ActivityLogView: FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const [filter, setFilter] = useState<"all" | ActivityCategory>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<
    "loading" | "ready" | "denied" | "error"
  >("loading");
  const [loadMore, { isFetching }] = useLazyGetFirmActivityQuery();
  // Ignore answers to older requests when the filter/search changed meanwhile.
  const requestRef = useRef(0);

  // Wait until typing pauses before searching.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPage = async (before?: string) => {
    const request = ++requestRef.current;
    const result = await loadMore({
      orgId,
      category: filter === "all" ? undefined : filter,
      search: search || undefined,
      before,
      take: PAGE_SIZE,
    });
    if (request !== requestRef.current) return;
    if (result.error) {
      const code = (result.error as { status?: number }).status;
      setStatus(code === 403 ? "denied" : "error");
      return;
    }
    const page = result.data || [];
    setEntries((current) => (before ? [...current, ...page] : page));
    setHasMore(page.length === PAGE_SIZE);
    setStatus("ready");
  };

  useEffect(() => {
    setStatus((current) => (current === "ready" ? current : "loading"));
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, filter, search]);

  return (
    <div className={"h-full min-w-0 flex-1 overflow-y-auto bg-[#F7F8FA]"}>
      <div className={"max-w-[920px] px-9 py-[30px]"}>
        <h1
          className={
            "mb-1 text-[24px] font-extrabold tracking-[-0.5px] text-[#1F2433]"
          }>
          Activity Log
        </h1>
        <p className={"mb-[22px] text-[14.5px] text-[#6B7185]"}>
          A running history of what changed across the firm — who did what, and
          when.
        </p>

        {status === "denied" ? (
          <div
            className={
              "rounded-[13px] border border-[#E8E8EC] bg-white p-[54px] text-center text-[14px] text-[#9097A8]"
            }>
            You don’t have permission to view the firm’s Activity Log. Ask an
            admin to turn on “View Activity Log” for you.
          </div>
        ) : (
          <>
            <div className={"mb-4 flex items-center gap-3"}>
              <span
                className={
                  "flex flex-1 items-center gap-[9px] rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-[13px] py-[9px]"
                }>
                <svg
                  width={"16"}
                  height={"16"}
                  viewBox={"0 0 24 24"}
                  fill={"none"}
                  stroke={"#9097A8"}
                  strokeWidth={"2"}
                  strokeLinecap={"round"}
                  strokeLinejoin={"round"}>
                  <circle cx={"11"} cy={"11"} r={"8"} />
                  <path d={"m21 21-4.3-4.3"} />
                </svg>
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={"Search by client or activity"}
                  className={
                    "flex-1 border-none bg-transparent text-[14px] text-[#1F2433] outline-none"
                  }
                />
              </span>
            </div>

            <div className={"mb-6 flex flex-wrap gap-2"}>
              {ACTIVITY_FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type={"button"}
                    onClick={() => setFilter(f.key)}
                    className={`cursor-pointer rounded-[20px] border-[1.5px] px-[14px] py-[7px] text-[13px] font-bold ${
                      active
                        ? "border-[#6B4EFF] bg-[#6B4EFF] text-white"
                        : "border-[#DCE2EA] bg-white text-[#5B6678] hover:border-[#6B4EFF]"
                    }`}>
                    {f.label}
                  </button>
                );
              })}
            </div>

            {status === "loading" && (
              <div className={"py-10 text-center text-[14px] text-[#9097A8]"}>
                Loading…
              </div>
            )}
            {status === "error" && (
              <div
                className={
                  "rounded-[13px] border border-[#E8E8EC] bg-white p-[54px] text-center text-[14px] text-[#9097A8]"
                }>
                The Activity Log couldn’t be loaded. Please try again.
              </div>
            )}
            {status === "ready" && entries.length === 0 && (
              <div
                className={
                  "rounded-[13px] border border-[#E8E8EC] bg-white p-[54px] text-center text-[14px] text-[#9097A8]"
                }>
                {search || filter !== "all"
                  ? "No activity matches your filters."
                  : "No activity recorded yet."}
              </div>
            )}
            {status === "ready" && entries.length > 0 && (
              <>
                <ActivityLogList entries={entries} />
                {hasMore && (
                  <div className={"flex justify-center"}>
                    <button
                      type={"button"}
                      disabled={isFetching}
                      onClick={() =>
                        fetchPage(entries[entries.length - 1].createdAt)
                      }
                      className={
                        "h-[36px] rounded-[8px] border border-[#DDE0E7] bg-white px-4 text-[13px] font-bold text-[#4A5163] hover:border-[#6B4EFF] hover:text-[#6B4EFF] disabled:opacity-60"
                      }>
                      {isFetching ? "Loading…" : "Show older activity"}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
